import { useRouter, Link, useNavigate } from "@tanstack/react-router";
import { Bell, Search, Menu, User, Settings, LogOut, X, Building2, BookOpen } from "lucide-react";
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { NAV_ITEMS, DUAL_AXIS_NAVIGATION, getActiveDomainAndTab } from "@/config/navigation";
import { SubHeaderNav } from "@/components/layout/SubHeaderNav";
import { HeaderSyncIndicator } from "@/components/layout/HeaderSyncIndicator";
import { useSidebar } from "@/layouts/RootLayout";
import { changelogData } from "@/config/changelog-data";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
    DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn, getUploadUrl } from "@/lib/utils";
import { authApi } from "@/lib/api-client";
import { trpcClient, queryClient } from "@/lib/trpc";
import { useAuthStore } from "@/stores/auth.store";
import { useSocket } from "@/hooks/useSocket";
import { usePageMode } from "@/hooks/usePageMode";
import { useDocHeaderStore } from "@/stores/useDocHeaderStore";
import { toast } from "sonner";

export function Header() {
    const router = useRouter();
    const pathname = router.state.location.pathname;
    const searchParams = router.state.location.search as Record<string, unknown>;
    const navigate = useNavigate();
    const { toggleSidebar } = useSidebar();
    const [searchValue, setSearchValue] = useState(typeof searchParams?.q === "string" ? searchParams.q : "");
    const [isMobileSearchOpen, setIsMobileSearchOpen] = useState(false);

    // Sync search input with URL q param
    useEffect(() => {
        setSearchValue(typeof searchParams?.q === "string" ? searchParams.q : "");
    }, [searchParams?.q, pathname]);

    // Find current page title with prioritized resolution
    const ROUTE_TITLE_MAP: Record<string, string> = {
        "/": "Dashboard",
        "/delivery": "Giao Hàng",
        "/deliveries": "Giao Hàng",
        "/debts": "Công Nợ KH",
        "/sales/debts": "Công Nợ KH",
        "/finance/ar": "Công Nợ KH",
        "/finance/ar-aging": "Báo Cáo Tuổi Nợ",
        "/purchasing/debts": "Công Nợ NCC",
        "/finance/ap": "Công Nợ NCC",
        "/finance/reconciliations": "Đối Soát Công Nợ",
        "/finance/ar/reconciliations": "Đối Soát Công Nợ",
        "/finance/ap/reconciliations": "Đối Soát Công Nợ",
        "/finance/receipts": "Thu Chi & Sổ Quỹ",
        "/purchasing": "Đơn Hàng Mua",
        "/purchase-orders": "Đơn Hàng Mua",
        "/molds": "Quản lý Khuôn",
        "/master/tooling": "Quản lý Khuôn",
        "/plates": "Bản Kẽm CTP",
        "/plates/ctp-logs": "Nhật Ký Kẽm CTP",
        "/production/ctp-tracking": "Nhật Ký Kẽm CTP",
        "/qc/ncr": "Sự Cố NCR",
        "/ncr": "Sự Cố NCR",
        "/qc/check": "QC Công Đoạn",
        "/qc-check": "QC Công Đoạn",
        "/qc/final": "FQC Thành Phẩm",
        "/qc-final": "FQC Thành Phẩm",
        "/factory/packing": "Đóng Gói & Nhập Kho",
        "/qc/templates": "Tiêu Chuẩn QC",
        "/qc-templates": "Tiêu Chuẩn QC",
        "/qc/material": "QC Nguyên Liệu",
        "/qc/process": "QC Quá Trình",
        "/qc-process": "QC Quá Trình",
        "/warehouses": "Danh Mục Kho",
        "/warehouse/master-data": "Danh Mục Kho",
        "/transactions": "Giao Dịch Kho",
        "/inventory/transactions": "Giao Dịch Kho",
        "/wip-transfers": "Điều Chuyển BTP",
        "/material-types": "Loại Vật Tư",
        "/product-types": "Loại Sản Phẩm",
        "/admin/config-master": "Cấu Hình Hệ Thống",
        "/trash": "Thùng Rác",
        "/guide": "Hướng Dẫn Sử Dụng",
        "/feedbacks": "Góp Ý & Phản Hồi",
        "/feedback": "Góp Ý & Phản Hồi",
        "/waste-matrix": "Định Mức Bù Hao",
        "/process-planning": "Kế Hoạch Sản Xuất",
        "/mes/andon": "Bảng Giám Sát Andon",
        "/mes/oee": "Hiệu Suất OEE",
        "/qc/reports": "Báo Cáo Chất Lượng",
        "/quality/reports": "Báo Cáo Chất Lượng",
        "/warehouse": "Quản Lý Kho",
        "/settings": "Cài Đặt Người Dùng",
        "/mes/incidents": "Sự Cố Thiết Bị",
        "/mes/maintenance": "Bảo Trì Bảo Dưỡng",
        "/production-scheduling": "Điều Độ Lịch Máy",
        "/material-planning": "Kế Hoạch Vật Tư",
        "/planning/paper-allocation": "Cấp Phát Giấy",
        "/tooling-tracking-board": "Bảng Theo Dõi Khuôn",
        "/reports": "Báo Cáo Quản Trị",
        "/reports/sales": "Báo Cáo Bán Hàng",
        "/reports/production": "Báo Cáo Sản Xuất",
        "/reports/inventory": "Báo Cáo Tồn Kho",
        "/reports/ar-aging": "Báo Cáo Tuổi Nợ",
        "/warehouse/inventory-nxt": "Báo Cáo NXT",
        "/warehouse/stocktake": "Kiểm Kê Kho",
        "/warehouse/fg": "Kho Thành Phẩm",
        "/warehouse/tech": "Kho Kỹ Thuật",
        "/warehouse/material": "Kho Vật Tư",
        "/warehouse/material/transactions": "Phiếu Nhập/Xuất Kho",
        "/factory/machine-logs": "Nhật Ký Máy",
        "/machine-logs": "Nhật Ký Máy",
        "/factory/production-logs": "Nhật Ký Sản Xuất",
        "/production-logs": "Nhật Ký Sản Xuất",
        "/shopfloor/terminal": "Màn Hình Xưởng",
        "/roles": "Phân Quyền Vai Trò",
        "/users": "Nhân Viên & Người Dùng",
        "/system/audit-logs": "Lịch Sử Hệ Thống",
        "/system/user-events": "Nhật Ký Sự Kiện",
        "/audit-logs": "Lịch Sử Hệ Thống",
        "/specs": "Hồ Sơ Kỹ Thuật",
        "/designs": "Thiết Kế Mẫu",
        "/layouts": "Bình File Prepress",
        "/production/prepress": "Bình File Prepress",
        "/production/print-layouts": "Bình File Prepress",
        "/production/prepress/create": "Tạo Bài Bình Prepress",
        "/production/prepress/new": "Tạo Bài Bình Prepress",
        "/processes": "Thư Viện Công Đoạn",
        "/qc-materials": "KCS Nguyên Vật Liệu",
        "/quality/materials": "KCS Nguyên Vật Liệu",
        "/wip-transfer": "Điều Chuyển BTP",
    };

    let searchString = "";
    if (searchParams && typeof searchParams === "object" && Object.keys(searchParams).length > 0) {
        const params = new URLSearchParams();
        Object.entries(searchParams).forEach(([k, v]) => {
            if (v !== undefined && v !== null) params.append(k, String(v));
        });
        searchString = `?${params.toString()}`;
    }

    const { activeTab } = getActiveDomainAndTab(pathname, searchString);
    let currentTitle = (activeTab && (pathname === '/materials' || pathname.startsWith('/materials/')))
        ? activeTab.title
        : (ROUTE_TITLE_MAP[pathname] || "");

    if (!currentTitle) {
        // Tra cứu trong DUAL_AXIS_NAVIGATION tabs
        if (activeTab) {
            currentTitle = activeTab.title;
        } else {
            for (const domain of DUAL_AXIS_NAVIGATION) {
                const foundTab = domain.tabs.find(t => t.href === pathname || (t.href !== '/' && pathname.startsWith(t.href + '/')));
                if (foundTab) {
                    currentTitle = foundTab.title;
                    break;
                }
            }
        }
    }

    if (!currentTitle) {
        for (const group of NAV_ITEMS) {
            if (group.href && (pathname === group.href || (group.href !== '/' && pathname.startsWith(group.href + '/')))) {
                currentTitle = group.title;
                break;
            }
            if (group.items) {
                const found = group.items.find(item => item.href === pathname || (item.href !== '/' && pathname.startsWith(item.href + '/')));
                if (found) {
                    currentTitle = found.title;
                    break;
                }
            }
        }
    }

    if (!currentTitle) {
        currentTitle = "Tổng quan";
    }

    const ROUTE_SEARCH_PLACEHOLDER_MAP: Record<string, string> = {
        "/factory/packing": "Tìm mã FQC, Lệnh SX, Số lô TP, Tên sản phẩm...",
    };
    const desktopSearchPlaceholder = ROUTE_SEARCH_PLACEHOLDER_MAP[pathname] || `Tìm kiếm trong ${currentTitle}...`;
    const mobileSearchPlaceholder = `Tìm trong ${currentTitle}...`;

    // Lấy user từ Zustand auth store
    const currentUser = useAuthStore((state) => state.user);
    const authLogout = useAuthStore((state) => state.logout);

    const user = {
        name: currentUser?.fullName || "Người dùng",
        email: currentUser?.email || "",
        role: currentUser?.role || "user",
        avatar: currentUser?.avatar || null,
    };

    // Lấy initials từ tên
    const initials = user.name.split(" ").map((w: string) => w[0]).join("").slice(0, 2).toUpperCase();

    // Lấy notifications từ tRPC API
    const { data: notifData, refetch: refetchNotifs } = useQuery({
        queryKey: ['notifications'],
        queryFn: async () => {
            try {
                return await trpcClient.notifications.list.query({ limit: 20 });
            } catch {
                return { items: [], meta: { total: 0 } };
            }
        },
        refetchInterval: 30000, // Tự động refresh mỗi 30 giây
    });

    const { socket } = useSocket();

    useEffect(() => {
        if (!socket) return;

        const handleNewNotification = (notification: { title?: string; message?: string; type?: string; link?: string } | null) => {
            refetchNotifs();
            const notifType = String(notification?.type || '').toUpperCase();
            if (notifType === 'ERROR' || notifType === 'CRITICAL') {
                toast.error(notification?.title || "Cảnh báo hệ thống", {
                    description: notification?.message,
                });
            } else if (notifType === 'WARNING') {
                toast.warning(notification?.title || "Lưu ý hệ thống", {
                    description: notification?.message,
                });
            }
        };

        socket.on('new_notification', handleNewNotification);

        return () => {
            socket.off('new_notification', handleNewNotification);
        };
    }, [socket, refetchNotifs, navigate]);

    // Auto-generate fallback notifications from changelog-data.ts
    // Track which version user has last seen via localStorage
    const lastSeenVersion = typeof window !== 'undefined' ? localStorage.getItem('apv_last_seen_changelog') : null;
    const fallbackNotifications = changelogData.slice(0, 5).map((release, idx) => {
        // Mark as unread if: no lastSeenVersion (first time) → only latest,
        // otherwise mark all versions newer than lastSeenVersion as unread
        const isUnread = lastSeenVersion
            ? release.version > lastSeenVersion
            : idx === 0;
        return {
            id: `sys-v${release.version.replace(/\./g, '')}`,
            title: `🔄 Cập nhật v${release.version} — ${release.title}`,
            message: release.changes.slice(0, 2).map(c => c.replace(/^[^\s]+ /, '').replace(/\*\*/g, '')).join(' | '),
            type: 'system',
            isRead: !isUnread,
            createdAt: release.date,
            link: '/changelog',
        };
    });

    const dbNotifs = (notifData as any)?.items || [];
    const combined = [...dbNotifs, ...fallbackNotifications.slice(0, 3)].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    const notifications = combined.slice(0, 20);
    const unreadCount = notifications.filter((n: any) => !n.isRead).length;

    // Mark changelog as seen when user opens the notification dropdown
    const handleNotificationOpen = () => {
        if (changelogData.length > 0) {
            localStorage.setItem('apv_last_seen_changelog', changelogData[0].version);
        }
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const val = e.target.value;
        setSearchValue(val);
        // Live update ?q and reset to page 1 so list pages filter immediately across all pages
        (navigate as any)({ search: (prev: any) => ({ ...prev, q: val || undefined, page: 1 }), replace: true });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            setSearchValue('');
            (navigate as any)({ search: (prev: any) => ({ ...prev, q: undefined, page: 1 }), replace: true });
        }
    };

    const { isHeaderVisible } = useSidebar();
    const pageMode = usePageMode();
    const isDocMode = pageMode === 'view' || pageMode === 'form';
    const hasDocHeader = useDocHeaderStore((state) => state.hasDocHeader);

    // ── Helper Renderers for Utilities ──
    const renderNotificationDropdown = (isCompact = false) => (
        <DropdownMenu onOpenChange={(open) => { if (open) handleNotificationOpen(); }}>
            <DropdownMenuTrigger asChild>
                <Button
                    variant="ghost"
                    size="icon"
                    className={cn(
                        "text-slate-500 hover:text-blue-600 hover:bg-blue-50 relative rounded-full",
                        isCompact ? "h-8 w-8" : "h-8.5 w-8.5 sm:h-9 sm:w-9"
                    )}
                >
                    <Bell className={cn(isCompact ? "h-4 w-4" : "h-4.5 w-4.5 sm:h-5 sm:w-5")} />
                    {unreadCount > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 flex items-center justify-center min-w-[16px] h-[16px] px-1 bg-rose-500 text-white text-[9px] font-bold rounded-full border-2 border-white shadow-xs">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-80 shadow-lg border-slate-200 rounded-md">
                <DropdownMenuLabel className="flex items-center justify-between px-4 py-3 bg-slate-50 rounded-t-md">
                    <span className="font-bold text-slate-700">Thông báo</span>
                    <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">{unreadCount} mới</span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="my-0" />
                <ScrollArea className="h-[320px]">
                    {notifications.length > 0 ? (
                        <div className="p-2 space-y-1">
                            {notifications.map((note: any) => (
                                <DropdownMenuItem key={note.id} className="cursor-pointer flex flex-col items-start gap-1 p-3 rounded-lg hover:bg-slate-50 focus:bg-slate-50 group border border-transparent hover:border-slate-100 transition-all" asChild>
                                    <Link to={note.link || "#"}>
                                        <div className="flex items-center justify-between w-full">
                                            <span className={cn("font-semibold text-sm group-hover:text-blue-600 transition-colors", !note.isRead ? "text-blue-600" : "text-slate-800")}>{note.title}</span>
                                            {!note.isRead && <span className="h-1.5 w-1.5 rounded-full bg-blue-600 shadow-sm" />}
                                        </div>
                                        <div 
                                            className="text-xs text-slate-500 line-clamp-2 my-1 leading-relaxed prose prose-sm prose-slate prose-p:my-0 prose-ul:my-0 prose-li:my-0 max-w-none"
                                            dangerouslySetInnerHTML={{ 
                                                __html: (note.message || '')
                                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                                    .replace(/\*(.*?)\*/g, '<em>$1</em>')
                                                    .replace(/\n/g, '<br />') 
                                            }}
                                        />
                                        <span className="text-[10px] text-slate-400 font-medium">
                                            {new Date(note.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })} {new Date(note.createdAt).toLocaleDateString()}
                                        </span>
                                    </Link>
                                </DropdownMenuItem>
                            ))}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-40 text-center space-y-2">
                            <Bell className="h-8 w-8 text-slate-200" />
                            <span className="text-sm text-slate-500 font-medium">Không có thông báo mới</span>
                        </div>
                    )}
                </ScrollArea>
                <DropdownMenuSeparator className="my-0" />
                <div className="p-2 bg-slate-50/50 rounded-b-xl">
                    <Button variant="ghost" size="sm" className="w-full text-xs font-bold text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-8" asChild>
                        <Link to="/changelog">Xem tất cả cập nhật</Link>
                    </Button>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );

    const renderUserDropdown = (isCompact = false) => (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <Button variant="ghost" className={cn("p-0 rounded-full ring-2 ring-white shadow-sm hover:ring-blue-100 transition-all overflow-hidden focus-visible:ring-blue-200", isCompact ? "h-8 w-8" : "h-9 w-9")}>
                    <Avatar className="h-full w-full">
                        <AvatarImage src={getUploadUrl(user.avatar) || "/avatars/user.png"} alt={user.name} />
                        <AvatarFallback className="apv-avatar-bg text-white text-[10px] sm:text-xs font-bold font-mono">{initials}</AvatarFallback>
                    </Avatar>
                </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64 p-0 shadow-lg border-slate-200 rounded-md overflow-hidden mt-2">
                <div className="bg-slate-50 p-4 border-b border-slate-200">
                    <div className="flex items-center gap-3">
                        <Avatar className="h-10 w-10 ring-2 ring-white shadow-sm">
                            <AvatarImage src={getUploadUrl(user.avatar) || "/avatars/user.png"} alt={user.name} />
                            <AvatarFallback className="apv-avatar-bg text-white font-bold">{initials}</AvatarFallback>
                        </Avatar>
                        <div className="flex flex-col overflow-hidden">
                            <span className="text-sm font-bold text-slate-900 truncate">{user.name}</span>
                            <span className="text-[11px] font-medium text-slate-500 truncate">{user.email}</span>
                            <Badge variant="secondary" className="mt-1 w-fit text-[9px] h-4 px-1.5 font-bold apv-accent-badge uppercase">
                                {user.role}
                            </Badge>
                        </div>
                    </div>
                </div>

                {/* Branch / Facility Selector in Menu */}
                <div className="px-3.5 py-2.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                        <Building2 className="h-4 w-4 text-indigo-600 shrink-0" />
                        <div className="flex flex-col min-w-0">
                            <span className="text-[10px] text-slate-500 font-medium leading-tight">Chi nhánh / Cơ sở</span>
                            <span className="text-xs font-bold text-slate-800 truncate">Nhà máy & Trụ sở Long An</span>
                        </div>
                    </div>
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0 bg-indigo-50 text-indigo-700 border-indigo-200 shrink-0 font-medium">
                        Mặc định
                    </Badge>
                </div>

                <div className="p-1.5">
                    <DropdownMenuGroup>
                        <DropdownMenuItem className="cursor-pointer gap-2 font-medium text-slate-700 focus:text-blue-600 focus:bg-blue-50 rounded-md" asChild>
                            <Link to="/profile">
                                <User className="h-4 w-4" /> Hồ sơ cá nhân
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="cursor-pointer gap-2 font-medium text-slate-700 focus:text-blue-600 focus:bg-blue-50 rounded-md" asChild>
                            <Link to="/settings">
                                <Settings className="h-4 w-4" /> Cài đặt tài khoản
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem className="cursor-pointer gap-2 font-medium text-indigo-700 focus:text-indigo-800 focus:bg-indigo-50 rounded-md" asChild>
                            <Link to="/guide" search={{ module: undefined }}>
                                <BookOpen className="h-4 w-4 text-indigo-600" /> Hướng dẫn sử dụng Go-Live
                            </Link>
                        </DropdownMenuItem>
                    </DropdownMenuGroup>

                    <DropdownMenuSeparator className="my-1.5 bg-slate-100" />

                    <DropdownMenuItem
                        className="cursor-pointer gap-2 text-rose-600 font-bold focus:text-rose-700 focus:bg-rose-50 rounded-lg"
                        onClick={async () => {
                            try {
                                await authApi.logout();
                            } catch {
                                // Tiếp tục đăng xuất dù API lỗi
                            }
                            authLogout();
                            queryClient.clear();
                            window.location.href = "/login";
                        }}
                    >
                        <LogOut className="h-4 w-4" /> Đăng xuất
                    </DropdownMenuItem>
                </div>
            </DropdownMenuContent>
        </DropdownMenu>
    );

    return (
        <header
            data-header-mode={isDocMode ? "document" : "list"}
            className={cn(
                "sticky top-0 z-40 flex flex-col w-full shadow-xs transition-transform duration-300 ease-in-out backdrop-blur-md border-b",
                // Mobile: slide up when hidden on scroll down
                !isHeaderVisible && "max-md:-translate-y-full max-md:pointer-events-none"
            )}
            style={{
                backgroundColor: 'color-mix(in srgb, var(--apv-header-bg, #ffffff) 95%, transparent)',
                borderColor: 'var(--apv-header-border, #dee2e6)',
                color: 'var(--apv-header-text, #212529)'
            }}
        >
            <div className="flex h-12 items-center justify-between gap-2 md:gap-4 px-2 sm:px-4 md:pl-1 md:pr-6 w-full border-b border-slate-200/60">
                {/* Mobile Expanded Search Bar Overlay (Only active in list mode when search opened) */}
                {isMobileSearchOpen && !isDocMode ? (
                    <div className="flex md:hidden items-center gap-2 w-full h-full animate-in fade-in-50 duration-150">
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                                <Search className="h-4 w-4 text-blue-600" />
                            </div>
                            <Input
                                type="search"
                                autoFocus
                                placeholder={mobileSearchPlaceholder}
                                value={searchValue}
                                onChange={handleInputChange}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape') setIsMobileSearchOpen(false);
                                    handleKeyDown(e);
                                }}
                                className="pl-8.5 pr-8 h-8 w-full bg-slate-100 border-slate-200 rounded-lg text-xs focus:bg-white"
                            />
                            {searchValue && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchValue('');
                                        (navigate as any)({ search: (prev: any) => ({ ...prev, q: undefined, page: 1 }), replace: true });
                                    }}
                                    className="absolute inset-y-0 right-0 flex items-center pr-2.5 text-slate-400 hover:text-slate-600"
                                >
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            )}
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 px-2.5 text-xs text-slate-600 hover:text-slate-900 shrink-0 font-medium"
                            onClick={() => setIsMobileSearchOpen(false)}
                        >
                            Đóng
                        </Button>
                    </div>
                ) : (
                    <>
                        {/* Left: Desktop Sidebar Toggle + Title */}
                        <div className="flex items-center gap-1.5 md:gap-3 shrink-0 min-w-0">
                            {/* Desktop: Sidebar Collapse Toggle */}
                            <Button
                                variant="ghost"
                                size="icon"
                                className="hidden md:flex text-slate-500 hover:text-slate-900 h-8 w-8"
                                onClick={toggleSidebar}
                            >
                                <Menu className="h-4.5 w-4.5" />
                            </Button>

                            {/* Render fallback title if in list mode OR doc mode before portal attaches */}
                            {(!isDocMode || !hasDocHeader) && (
                                <h1 className="text-xs sm:text-sm md:text-base font-bold text-[#212529] tracking-tight leading-none whitespace-nowrap truncate max-w-full">
                                    {currentTitle}
                                </h1>
                            )}
                        </div>

                        {/* Center: Doc Mode Portal Container OR List Mode Contextual Search */}
                        {isDocMode ? (
                            <div
                                id="apv-header-portal-slot"
                                className="flex-1 flex items-center justify-between gap-2 md:gap-3 min-w-0 h-full overflow-hidden"
                            />
                        ) : (
                            <div className="flex-1 hidden md:flex justify-center max-w-4xl mx-auto px-4">
                                {!pathname.startsWith("/factory/operator") && !pathname.startsWith("/factory/shopfloor") && !pathname.startsWith("/factory/production-scheduling") && !pathname.startsWith("/factory/machines") && !pathname.startsWith("/factory/machine-logs/") && pathname !== "/" && (
                                    <div className="relative w-full max-w-lg group">
                                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                            <Search className="h-4 w-4 text-slate-500 group-focus-within:text-blue-600 transition-colors" />
                                        </div>
                                        <Input
                                            type="search"
                                            placeholder={desktopSearchPlaceholder}
                                            value={searchValue}
                                            onChange={handleInputChange}
                                            onKeyDown={handleKeyDown}
                                            className="pl-10 h-8 w-full bg-slate-100 border-slate-200 rounded-md focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all text-xs"
                                        />
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Right: Actions Bar (QuickSearch if list, Sync, Notification, Avatar) */}
                        <div className="flex items-center gap-1 sm:gap-2 shrink-0 ml-auto">
                            {!isDocMode && (
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    className="md:hidden text-slate-600 hover:text-blue-600 hover:bg-blue-50 rounded-full h-8 w-8"
                                    onClick={() => setIsMobileSearchOpen(true)}
                                    title="Tìm kiếm nhanh"
                                >
                                    <Search className="h-4 w-4" />
                                </Button>
                            )}

                            <HeaderSyncIndicator />
                            {renderNotificationDropdown(true)}
                            <div className="h-4 w-px bg-slate-200 mx-0.5 hidden md:block" />
                            {renderUserDropdown(true)}
                        </div>
                    </>
                )}
            </div>

            {/* Sub-Header Horizontal Process Tabs: ONLY in List Mode (Hidden in View/Form Mode!) */}
            {!isDocMode && <SubHeaderNav />}
        </header>
    );
}
